/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2020, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types',
	'../../../app/nls/StringResources'
], function(
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes,
	StringResources
) {

	const CLASSNAME = 'properties--controls--InputLabel';

	const getUISpec = (props) => {
		const { item } = props;
		let spec = item;
		if (item && item.editor && item.editor.uiControl) {
			spec = item.editor.uiControl;
		}
		return spec;
	};

	/**
	 * @class InputLabel
	 * @description InputLabel property control class. Supports 2 types of input: regular text and numeric input
	 * 
	 * @example Example props usage to render a number input:
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					placeHolderText: 'no value here',
	 *					readOnly: false,
	 *					inputType: 'number', // defaults to 'text'
	 *					decimalPlaces: 2
	 *				}
	 *			},
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 *			value: 23  // required field
	 *		},
	 *		style: {
	 *			width: '140px'
	 *		},
	 *		className: 'myControlClass'
	 * }
	 * 
	 * @example Example props usage to render a text input:
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					placeHolderText: 'no value here',
	 *					readOnly: false
	 *				}
	 *			},
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 *			value: 23 // required field
	 *		},
	 *		style: {
	 *			width: '140px'
	 *		},
	 *		className: 'myControlClass'
	 * }
	 */
	class InputLabel extends React.Component {

		constructor(props) {
			super(props);

			this.state = {
				_forceRefreshKey: 0
			};
			this._controlKey = 0;
			this._refInput = React.createRef();

			// delay so user can see the typed value 
			// before it's reset to the processed or min/max value
			this._delay = props.delay || 1000; // make it an option for unit test
		}

		render() {
			const { style, item } = this.props;
			let { className } = this.props;
			const { inputType } = getUISpec(this.props);
			className += `${className ? ' ' : ''}${CLASSNAME}--${inputType || 'text'}`;
			const { id } = item;
			const containerProps = {
				style,
				className,
				'data-property-id': id
			};
			return (
				<div {...containerProps}>
					{this.renderControl()}
				</div>
			);
		}

		renderControl() {
			const { item } = this.props;
			const { setPropertyValue, value } = item;
			const {
				label,
				placeHolderText,
				readOnly,
				inputType,
				decimalPlaces,
				min,
				max,
				invalidText = StringResources.get('propInputLabel_number_invalid')
			} = getUISpec(this.props);

			switch(inputType) {
				case 'number': {
					const normalize = (value) => {
						let result = Number(value);
						const valueString = String(value);
						if (decimalPlaces !== undefined && valueString.indexOf('.') !== -1) {
							const valueSplit = valueString.split('.');
							if (valueSplit[1].length > 0) {
								if (decimalPlaces === 0) {
									result = Math.round(result);
								} else if (valueSplit[1].length > decimalPlaces) {
									result = Number(result.toFixed(decimalPlaces));
								}
							}
						}
						return result;
					};

					const normalizedValue = normalize(value);
					const minValue = min != null ? { min } : null;
					const maxValue = max != null ? { max } : null;

					return (
						<BA_UI_CARBON.NumberInput
							ref={this._refInput}
							key={this._controlKey++}
							invalidText={invalidText}
							label={label}
							value={normalizedValue}
							disabled={readOnly}
							{...minValue}
							{...maxValue}
							onChange={(event) => {
								const value = event.imaginaryTarget.value;
								// do not process empty value when user remove the whole value in input
								if (value.length > 0) {
									setTimeout(() => {
										const processedValue = normalize(value);
										setPropertyValue(processedValue);
										// mobx only re-renders the component when the value changed to something different than the initial value
										// ex: value = '123.123'; processedValue = '123.12'; this.props.item.value = '123.12';
										// the number input will show '123.123' in the UI
										if (processedValue === normalizedValue) {
											this._forceRefresh();
										}
									}, this._delay);
								}
							}}
							light
						/>
					);
				}
				case 'text':
				default:
					return (
						<BA_UI_CARBON.TextInput
							ref={this._refInput}
							value={value}
							labelText={label}
							placeholder={placeHolderText}
							disabled={readOnly}
							onChange={(event) => {
								setPropertyValue(event.currentTarget.value);
							}}
							light
						/>
					);
			}
		}

		_resetFocus() {
			this._refInput.current.focus();
		}

		_forceRefresh() {
			this.setState({
				_forceRefreshKey: this.state._forceRefreshKey + 1
			}, this._resetFocus);
		}
	}

	InputLabel.propTypes = {
		item: PropTypes.shape({
			editor: PropTypes.shape({
				uiControl: PropTypes.shape({
					id: PropTypes.string,
					label: PropTypes.string,
					placeHolderText: PropTypes.string,
					readOnly: PropTypes.bool,
					inputType: PropTypes.string,
					invalidText: PropTypes.string,
					decimalPlaces: PropTypes.number,
					min: PropTypes.number,
					max: PropTypes.number
				})
			}),
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			placeHolderText: PropTypes.string,
			readOnly: PropTypes.bool,
			setPropertyValue: PropTypes.func.isRequired,
			value: PropTypes.string

		}),
		style: PropTypes.object,
		className: PropTypes.string,
		delay: PropTypes.number
	};
	InputLabel.defaultProps = {
		style: {},
		className: ''
	};

	return MobxReact.observer(InputLabel);
});